/** Public, reviewed product contract for the first maintained fresh V2 path.
 * Release/compiler/component identities are supplied separately by the trusted
 * release loader; this module owns only the creator-visible gameplay promise. */
import { canonicalDigest, canonicalJson, freezeJson, validateSha256 } from '@djgg/gameplay-kit';
import type { GameQualityRequirementV2 } from '../qualitySchemas.js';
import { validateAcceptedCompositionV2, type AcceptedCompositionV2 } from './compositionV2.js';

export const MAINTAINED_TANK_V2_CONTRACT_REVISION = 'maintained-tank-multimode@1' as const;
export const MAINTAINED_TANK_REQUIREMENT_ID_V2 = 'tank-seat-agency' as const;
/** The durable-control profile id that admits the maintained Tank game
 * (Arena Tanks). Every maintained Tank release's publication target names it. */
export const MAINTAINED_TANK_ADMISSION_PROFILE_V2 = 'maintained-tank@1' as const;
/** The public serving destination id every maintained Tank publication uses. */
export const MAINTAINED_TANK_PUBLIC_DESTINATION_ID_V2 = 'maintained-public-v2' as const;

export const MAINTAINED_TANK_PLAY_V2: AcceptedCompositionV2['play'] = freezeJson({
  orientation: 'landscape',
  modes: [
    { mode: 'human-cpu', players: 2, teams: 'individual', cpu: 'seat-1', devices: ['desktop', 'touch'] },
    { mode: 'local', players: 2, teams: 'individual', cpu: 'none', devices: ['desktop'] },
    { mode: 'online', players: 2, teams: 'individual', cpu: 'none', devices: ['desktop', 'touch'] },
  ],
  requirementApplicability: [{ requirementId: MAINTAINED_TANK_REQUIREMENT_ID_V2,
    modes: ['human-cpu', 'local', 'online'], devices: ['desktop', 'touch'], stages: ['arena'] }],
});

export const MAINTAINED_TANK_OBLIGATIONS_V2: AcceptedCompositionV2['obligations'] = freezeJson({
  stages: { applicable: true, ids: ['arena'] },
  campaign: { applicable: false },
  loadout: { applicable: false },
  media: { applicable: false, kind: 'coded', slots: [] },
});

export const MAINTAINED_TANK_DISCLOSURE_V2 = freezeJson({
  schemaVersion: 2 as const,
  kind: 'maintained-composition-contract' as const,
  revision: MAINTAINED_TANK_V2_CONTRACT_REVISION,
  title: 'Two-seat arena tanks',
  orientation: 'landscape' as const,
  modes: [
    { mode: 'local' as const, players: 2 as const, devices: ['desktop'] as const },
    { mode: 'human-cpu' as const, players: 2 as const, devices: ['desktop', 'touch'] as const },
    { mode: 'online' as const, players: 2 as const, devices: ['desktop', 'touch'] as const },
  ],
  mechanics: ['independent-body-movement', 'independent-turret-aim', 'owned-projectiles',
    'victim-damage', 'round-scoring', 'restart'] as const,
  stages: ['arena'] as const,
  media: { kind: 'coded' as const, generatedSlots: [] as const },
  unsupported: ['campaign', 'loadout', 'portrait', 'uploaded-media', 'generated-media',
    'more-than-two-seats', 'shared-touchscreen'] as const,
});

/** Bind the maintained law to the exact creator-reviewed design. */
export function createMaintainedTankRequirementV2(designDigest: string): GameQualityRequirementV2 {
  validateSha256(designDigest, 'maintainedTank.designDigest');
  return freezeJson({
    id: MAINTAINED_TANK_REQUIREMENT_ID_V2,
    mandatory: true,
    provenance: { source: 'approved-design', reference: `${MAINTAINED_TANK_V2_CONTRACT_REVISION}:${designDigest}` },
    semantic: { kind: 'agency', revision: 1, parameters: {} },
    actors: ['seat-0', 'seat-1'],
    targets: ['tank-0', 'tank-1'],
    preconditions: ['active'],
    actions: ['body-move', 'body-turn', 'fire', 'turret-turn'],
    consequences: ['owned-projectile', 'victim-damage', 'victor-point'],
    forbiddenOutcomes: ['inactive-seat-agency', 'wrong-seat-agency'],
    obligations: [
      { stage: 'final', evidenceKind: 'deterministic-simulation', device: 'deterministic' },
      { stage: 'final', evidenceKind: 'native-browser', device: 'desktop' },
      { stage: 'final', evidenceKind: 'native-browser', device: 'touch' },
    ],
  });
}

export async function maintainedTankDisclosureDigestV2(): Promise<string> {
  return canonicalDigest(MAINTAINED_TANK_DISCLOSURE_V2);
}

/** Authenticate only the exact fresh product promise. Compiler, release and
 * registry authentication remain independent trusted boundaries. */
export async function assertMaintainedTankFreshAuthorityV2(
  input: AcceptedCompositionV2,
): Promise<AcceptedCompositionV2> {
  const authority = await validateAcceptedCompositionV2(input);
  const requirement = createMaintainedTankRequirementV2(authority.intent.designDigest);
  if (authority.version !== 1 || authority.predecessor !== null
    || canonicalJson(authority.intent.requirements) !== canonicalJson([requirement])
    || authority.intent.requirementsDigest !== await canonicalDigest([requirement])
    || canonicalJson(authority.play) !== canonicalJson(MAINTAINED_TANK_PLAY_V2)
    || canonicalJson(authority.obligations) !== canonicalJson(MAINTAINED_TANK_OBLIGATIONS_V2)) {
    throw new Error('Maintained Tank V2: accepted gameplay contract changed');
  }
  return authority;
}
